import { Affix, Button, Card, Col, Form, Input, Row, Space, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import axios from 'axios';
import { useContext, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { CiLocationOn } from 'react-icons/ci';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { useHistory, useLocation } from 'react-router-dom';

import AddressUnitsSelector from '../../components/common/AddressUnitSelector';
import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import { clearObjFields } from '../../utils';
import { isHandledByInterceptor } from '../../utils/apiError';
import { ClientContext } from './ClientContext';
import './style.less';
import {
  CUSTOMER_NAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  PHONE_NUMBER_LENGTH,
  emailRules,
  phoneNumberRules,
} from '../../utils/formRules';

const LIST_PATH = '/clients';

/**
 * Формын талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдаж,
 * баруун талд зай үлдэнэ. Карт бүрд ижил өргөн тул зүүн ирмэг нь эгнэнэ.
 */
const FORM_MAX_WIDTH = 960;

/** Картын гарчиг: зүүн талд дүрс */
const CardTitle = ({ icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    {icon}
    {children}
  </span>
);

const AddClient = ({ onCancel = () => undefined }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { pathname } = useLocation();
  const { refreshList } = useContext(ClientContext);

  const [addressUnits, setAddressUnits] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const routes = [
    { to: { pathname: LIST_PATH }, breadcrumbName: 'Харилцагчид' },
    { path: 'first', breadcrumbName: 'Шинэ харилцагч' },
  ];

  const goBack = () => history.push(LIST_PATH);

  const onOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // Талбарын алдааг Form өөрөө харуулна
      return;
    }

    setSubmitting(true);
    try {
      const reqData = clearObjFields({
        ...values,
        address: addressUnits,
        phoneNumber: `${values?.phoneNumber}`,
      });

      const { data } = await axios.post('/api/v1/merchant-customer', reqData);

      if (data?.statusCode === 200) {
        message.success('Харилцагч амжилттай үүслээ.');
        if (pathname === LIST_PATH) refreshList();
        onCancel();
        history.push(LIST_PATH);
        return;
      }

      // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
      if (!isHandledByInterceptor(data)) {
        message.error(data?.message ?? 'Харилцагч үүсгэхэд алдаа гарлаа.');
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Create client error: ', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PPPageHeader title="Харилцагч нэмэх" breadcrumb={{ routes }} onBack={goBack} />

      <Form layout="vertical" form={form}>
        <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
          <Card
            size="middle"
            title={<CardTitle icon={<AiOutlineInfoCircle />}>Ерөнхий мэдээлэл</CardTitle>}
          >
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={24} sm={12} xl={8}>
                <Form.Item
                  name="name"
                  label="Харилцагчийн нэр"
                  rules={[{ required: true, message: 'Харилцагчийн нэр дутуу байна!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Энд бич" maxLength={CUSTOMER_NAME_MAX_LENGTH} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8}>
                <Form.Item
                  name="phoneNumber"
                  label="Харилцагчийн утас"
                  rules={phoneNumberRules()}
                  style={{ marginBottom: 0 }}
                >
                  {/* `inputMode` нь гар утсан дээр тоон гар нээнэ */}
                  <Input
                    placeholder="Энд бич"
                    maxLength={PHONE_NUMBER_LENGTH}
                    inputMode="numeric"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8}>
                <Form.Item
                  name="email"
                  label="Харилцагчийн имэйл"
                  rules={emailRules('Харилцагчийн имэйл дутуу байна!')}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Энд бич" maxLength={EMAIL_MAX_LENGTH} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="middle"
            title={<CardTitle icon={<CiLocationOn />}>Харилцагчийн хаяг</CardTitle>}
          >
            <div style={{ maxWidth: FORM_MAX_WIDTH }}>
              <AddressUnitsSelector selectedUnits={addressUnits} onChange={setAddressUnits} />

              <Form.Item
                name="addressDetail"
                label="Харилцагчийн дэлгэрэнгүй хаяг"
                style={{ marginBottom: 0 }}
              >
                <TextArea placeholder="Энд бич" rows={4} />
              </Form.Item>
            </div>
          </Card>

          <Card
            size="middle"
            title={<CardTitle icon={<IoDocumentTextOutline />}>Нэмэлт мэдээлэл</CardTitle>}
          >
            <Form.Item name="description" style={{ marginBottom: 0, maxWidth: FORM_MAX_WIDTH }}>
              <TextArea placeholder="Энд бич" rows={4} />
            </Form.Item>
          </Card>

          <Affix offsetBottom={16}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                padding: 12,
                backgroundColor: '#fff',
                border: '1px solid #f1f2f4',
                borderRadius: 8,
                boxShadow: '0 1px 2px rgba(16, 22, 26, 0.04)',
              }}
            >
              <Button onClick={goBack}>Буцах</Button>
              <Button type="primary" loading={submitting} onClick={onOk}>
                Шинээр нэмэх
              </Button>
            </div>
          </Affix>
        </Space>
      </Form>
    </div>
  );
};

export default AddClient;
